/**
 * ``useProductQuote`` — debounced, server-authoritative price preview for one product.
 *
 * Whenever the requested spend changes, re-quotes via ``POST /api/products/<id>/quote/``
 * (debounced so typing doesn't spam the server). The returned ``quote`` is the single
 * source of truth for the total and per-currency maximums; a ``LimitExceededError`` surfaces
 * in ``error`` so the page can block ordering until the spend is valid.
 */
import { ApiError } from "@shared/api";
import { useEffect, useState } from "react";

import { quoteProduct } from "./api";
import type { ProductQuote } from "./model";

/** Debounce window for re-quoting while the user edits the spend fields. */
const QUOTE_DEBOUNCE_MS = 300;

export interface ProductQuoteState {
  /** Latest successful quote (kept across a failing edit for display continuity). */
  readonly quote: ProductQuote | null;
  readonly error: ApiError | null;
  readonly isLoading: boolean;
}

export function useProductQuote(
  productId: number,
  starGlitterSpend: number,
  primogemSpend: number,
): ProductQuoteState {
  const [quote, setQuote] = useState<ProductQuote | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const handle = setTimeout(() => {
      quoteProduct(productId, {
        star_glitter_spend: starGlitterSpend,
        primogem_spend: primogemSpend,
      })
        .then((next) => {
          if (cancelled) {
            return;
          }
          setQuote(next);
          setError(null);
        })
        .catch((err: unknown) => {
          if (cancelled) {
            return;
          }
          setError(
            err instanceof ApiError ? err : new ApiError(0, "Network error", err),
          );
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [productId, starGlitterSpend, primogemSpend]);

  return { quote, error, isLoading };
}
